import { UseMaterialColors } from "lib/hooks";

import { Theme, alpha } from "@mui/material/styles";


// Shared typography scale for tables
export const TableFontScale = {
    default: { header: "0.9rem", cell: "0.9rem" },
    small: { header: "0.75rem", cell: "0.875rem" },
} as const;

export function GetSharedTableStyles(variant: "default" | "small" = "default") {
    const materialColors = UseMaterialColors();

    const baseFontSize =
        variant === "default"
            ? TableFontScale.default.cell
            : TableFontScale.small.cell;
    const headerFontSize =
        variant === "default"
            ? TableFontScale.default.header
            : TableFontScale.small.header; // Match BaseTable small variant

    return {
        root: {
            borderCollapse: "separate",
            borderSpacing: 0,

            "& .MuiTableCell-root": {
                px: 0,
                py: 1,
                fontSize: baseFontSize,

                borderBottom: `1px solid ${alpha(materialColors.outline, 0.06)}`,
            },
            "& .MuiTableBody-root .MuiTableCell-root": {
                px: 1.5,
                textAlign: "left",
            },
            "& .MuiTableBody-root .MuiTableCell-root.MuiTableCell-alignRight": {
                textAlign: "right",
                paddingRight: 2,
            },
            "transition": "background-color 0.2s ease",
            "& .MuiTableCell-head": {
                textAlign: "left",
                pr: 1,
                pl: 2,
                fontWeight: 700,
                backgroundColor: materialColors.surfaceContainerHigh,
                fontSize: headerFontSize,
                letterSpacing: "0.01em",
                borderBottom: `1px solid ${alpha(materialColors.outline, 0.08)}`,
                position: "sticky",
                top: 0,
                zIndex: 1,
                borderRadius: 0,
                boxShadow: `-1px 0 0 0 ${materialColors.surfaceContainerHigh}`,
            },
            "& .MuiTableCell-head.MuiTableCell-alignRight": {
                textAlign: "right",
                paddingRight: 2,
            },
            "& .MuiTableSortLabel-root.Mui-active": {
                color: materialColors.primary,
            },
            "& .MuiTableSortLabel-icon": {
                color: materialColors.primary,
            },
            "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
                backgroundColor: alpha(materialColors.surfaceContainerHigh, 0.3),
            },
            "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)": {
                backgroundColor: alpha(materialColors.surfaceContainer, 0.4),
            },
            "& .MuiTableBody-root .MuiTableRow-root:hover": {
                backgroundColor: alpha(materialColors.surfaceContainerHigh, 1),
            },
            "& .MuiTableBody-root .MuiTableRow-root.Mui-selected": {
                backgroundColor: alpha(materialColors.secondary, 0.28),

                "&:hover": {
                    backgroundColor: alpha(materialColors.secondary, 0.28),
                },

                "& .MuiTableCell-root": {
                    border: `1px solid ${materialColors.secondary}`,
                    fontWeight: 500,
                },
            },
        },
        cellSkeleton: {
            width: 30,
            height: 20,
            mx: "auto",
            borderRadius: 1,
        },
    };
}


export function TableContainerStyles() {
    const materialColors = UseMaterialColors();
    return {
        overflowX: "auto",
        boxShadow: materialColors.elevation.elevation1,
        border: `1px solid ${alpha(
            materialColors.outline,
            materialColors.isDark ? 0.1 : 0.05
        )}`,
        transition: "all 0.3s ease",
        bgcolor: alpha(
            materialColors.surfaceContainer,
            materialColors.isDark ? 0.6 : 0.8
        ),
        borderRadius: 0,

    } as const;
}

// Shared typography tokens for tables
export function TableTypography(variant: "default" | "small" = "small") {
    // Align with BaseTable (small uses 0.875rem for both)
    const headerFontSize =
        variant === "default"
            ? TableFontScale.default.header
            : TableFontScale.small.header;
    const cellFontSize =
        variant === "default"
            ? TableFontScale.default.cell
            : TableFontScale.small.cell;
    const headerLetterSpacing = "0.01em";
    const cellLetterSpacing = "0.01em";
    const headerFontWeight = 700; // bold
    const cellFontWeight = 400;
    return {
        headerFontSize,
        cellFontSize,
        headerLetterSpacing,
        cellLetterSpacing,
        headerFontWeight,
        cellFontWeight,
    } as const;
}

// Shared hover color for header sort labels
export function TableHeaderHoverColor() {
    const m = UseMaterialColors();
    return m.primary;
}

// Shared sx for MUI TableSortLabel in table headers
export function TableSortLabelSx(active: boolean) {
    const hoverColor = TableHeaderHoverColor();
    return {
        wordBreak: "keep-all",
        flexDirection: "row",
        "& .MuiTableSortLabel-icon": {
            opacity: active ? 1 : 0,
            transition: "all 0.25s ease",
            color: hoverColor,
        },
        "&:hover .MuiTableSortLabel-icon": {
            opacity: 0.7,
        },
        "&:hover": {
            color: hoverColor,
        },
        transition: "all 0.25s ease",
    } as const;
}



export const GadgeStyleLable = (theme: Theme) => ({
    display: "inline-block",
    width: "fit-content",
    padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,   // px / py
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.primary.light}`,
    backgroundColor: theme.palette.primary[50],
    color: theme.palette.primary.main,
    borderRadius: 9999,          // pill-shape
    fontSize: "0.85rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
});

export const TableHeaderStyles = (theme: Theme) => ({
    "& .MuiTableHead-root": {
        "& .MuiTableCell-root": {
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            fontWeight: 800,
            borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
            color: alpha(theme.palette.text.primary, 0.8),
            padding: theme.spacing(1.5, 2),
            fontSize: TableFontScale.default.header,
            letterSpacing: "0.02em",
        },
        "& .MuiTableSortLabel-root.Mui-active": {
            color: theme.palette.primary.main,
        },
    },
    "wordBreak": "break-word",
})

// Shared cell styles for user management and contact info tables
export function GetTableCellStyles() {
    const materialColors = UseMaterialColors();
    return {
        name: {
            fontWeight: 500,
            color: materialColors.onSurface,
        },
        email: {
            color: materialColors.primary,
            textDecoration: "none",
            "&:hover": {
                textDecoration: "underline",
            },
        },
        phone: {
            color: materialColors.onSurfaceVariant,
        },
    } as const;
}